/**
 * `/stock-list/complete`: where PayPal sends a Stock List buyer after they approve the payment,
 * as `?paymentId=…&PayerID=…` (`web-payments-api/application/src/enums/paypal.enum.ts` →
 * `EPaypalRedirectUrl.EcomStocklistReturnUrl`). Ryerson build only.
 *
 * On mount it posts `{ paymentId, payerId }` to `APIUrl.STOCK_LIST_ORDER_ACKNOWLEDGEMENT`,
 * served by `web-payments-api/application/src/functions/PostPaypalOrderStocklistAcknowledgement/`.
 * That call is what completes the purchase: the Payments API executes the PayPal payment (the
 * buyer is charged then), queues the confirmation email and marks the order complete. A buyer who
 * doesn't reach this page, or whose browser doesn't run it, has approved a payment that is never
 * taken.
 *
 * Rules a change must keep:
 * - The path, and the `paymentId` and `PayerID` query names, are PayPal's and the Payments API's;
 *   they don't change on this side alone.
 * - The acknowledgement runs once per page load. A reload posts it again, and the Payments API
 *   answers 500 for a `paymentId` it has already completed, so a reload doesn't charge twice.
 *
 * The thank-you copy renders whatever the acknowledgement's outcome: a failed call is only logged
 * to the console, and a visit without `paymentId` posts nothing and shows the same page.
 *
 * Related: `components/StockList/Purchase.tsx` (the first half of the purchase),
 * `components/StockList/Complete.tsx` (the thank-you copy),
 * `content/ryerson/stock-list/complete.ts`.
 */
import React from "react";
import { useApplication } from "@ryerson/frontend.application";
import MetaHead from "@components/Shared/model/MetaHead";
import Hero from "@components/StockList/Complete";
import { APIUrl } from "@enums/apiUrl.enums";
import { MetaObject } from "@content/ryerson/stock-list/complete";
import { ThankYou } from "@content/ryerson/stock-list/complete";
import { allContentfulHeaderCalls, HeaderPageProps } from "@ryerson/frontend.contentful";
import { getHeaderData } from "@components/NextLayouts/nextUtilities";
import HeaderLayout from "@components/NextLayouts/HeaderLayout";

/**
 * Builds the page on the Ryerson build only and fetches the Contentful header, locations and
 * footer data for `HeaderLayout`.
 */
export const getStaticProps = async () => {
	if (process.env.NEXT_PUBLIC_COMPANY !== "ryerson") {
		return { notFound: true }; // tells Next.js to skip page generation and return 404
	}

	return await allContentfulHeaderCalls();
};

/** The `/stock-list/complete` page; see the file header. */
const Page: React.FC<HeaderPageProps> = (props) => {
	const {
		localization: { language },
		axiosInstance,
	} = useApplication();

	React.useEffect(() => {
		sendPaypalOrderStocklistAcknowledgement();
	}, []);

	/**
	 * Asks the Payments API to execute the approved PayPal payment named in the URL. Does nothing
	 * when the URL has no `paymentId`. Never throws: a failure is logged with `console.error`.
	 */
	const sendPaypalOrderStocklistAcknowledgement = async () => {
		const queryString = window.location.search;
		if (queryString) {
			const urlParams = new URLSearchParams(queryString);

			if (urlParams.get("paymentId")) {
				const paymentId = urlParams.get("paymentId");
				const payerId = urlParams.get("PayerID");
				const requestBody = {
					paymentId: paymentId,
					payerId: payerId,
				};
				await axiosInstance
					.post(`${APIUrl.STOCK_LIST_ORDER_ACKNOWLEDGEMENT}`, requestBody)
					.then((response: any) => {})
					.catch((error) => {
						console.error(
							`Failed to send stock list order acknowledgement email  ${error}`
						);
					});
			}
		}
	};

	return (
		<HeaderLayout {...getHeaderData(props)}>
			<MetaHead content={MetaObject[language]} />
			<Hero content={ThankYou[language]} />
		</HeaderLayout>
	);
};

export default Page;
